import React, { Fragment, useState, useEffect } from 'react';
import { Container, TabContent, TabPane, Nav, NavItem, NavLink, Card, CardBody, Row, Col } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchFormData } from '../../../../Api/ApiMethods';
import AccidentFileInformations from './AccidentFileInformations';
import ArbitrationInformations from './ArbitrationInformations';
import InterlocutoryRulingInformations from './InterlocutoryRulingInformations';
import ExpertWitnessReportInformations from './ExpertWitnessReportInformations';
import RulingInformations from './RulingInformations';
import ReapplicationInformations from './ReapplicationInformations';

const Arbitration = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('1');
    const [arbitrationData, setArbitrationData] = useState({});
    const [loading, setLoading] = useState(true);

    // API endpoint
    const arbitrationApiUrl = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/arbitration/${id}`;

    // Tab definitions
    const tabs = [
        { id: '1', title: 'Kaza Dosyaları', icon: 'fa fa-folder' },
        { id: '2', title: 'Tahkim Bilgileri', icon: 'fa fa-info-circle' },
        { id: '3', title: 'Ara Kararlar', icon: 'fa fa-gavel' },
        { id: '4', title: 'Bilirkişi Raporları', icon: 'fa fa-file-text' },
        { id: '5', title: 'Kararlar', icon: 'fa fa-check-circle' },
        { id: '6', title: 'Tekrar Başvuru Bilgileri', icon: 'fa fa-repeat' }
    ];

    // Function to determine which tabs should be visible based on arbitration state and existing data
    const getVisibleTabIds = () => {
        const stateTitle = arbitrationData?.arbitrationState?.title;
        
        // Her zaman görünür olan temel tablar
        let visibleTabs = ['1', '2']; // Kaza Dosyaları, Tahkim Bilgileri
        
        if (!stateTitle) {
            // Eğer state yoksa sadece temel tabları döndür
            return visibleTabs;
        }

        // State'e göre yeni eklenebilecek tablar
        switch (stateTitle) {
            case 'Ara Karar Bekleniyor':
            case 'Bilirkişi Ödemesi Bekleniyor':
                visibleTabs.push('3'); // Ara Kararlar
                break;
            
            case 'Bilirkişi Raporu Bekleniyor':
            case 'Islah Ödemesi Bekleniyor':
                visibleTabs.push('3', '4'); // Ara Kararlar + Bilirkişi Raporları
                break;
            
            case 'Karar Bekleniyor':
            case 'Karşı Vekalet Ödemesi Bekleniyor':
            case 'İtiraz Ücreti Ödemesi Bekleniyor':
                visibleTabs.push('3', '4', '5'); // Ara Kararlar + Bilirkişi Raporları + Kararlar
                break;
            
            case 'Tekrar Başvuru Bekleniyor':
            case 'Kapama Bekleniyor':
                visibleTabs.push('3', '4', '5', '6'); // Tüm tablar
                break;
            
            default:
                // Default case - sadece temel tablar
                break;
        }

        // VERİ VARLIĞI KONTROLÜ - Eğer veri varsa o tabları da göster
        // Bu sayede state geri gittiğinde bile önceki veriler görünür kalır
        
        // Ara Kararlar verisi varsa Tab 3'ü göster
        if (arbitrationData?.interlocutoryRulingInformation && arbitrationData.interlocutoryRulingInformation.length > 0) {
            if (!visibleTabs.includes('3')) {
                visibleTabs.push('3');
            }
        }
        
        // Bilirkişi Raporları verisi varsa Tab 4'ü göster
        if (arbitrationData?.expertWitnessReportInformation && arbitrationData.expertWitnessReportInformation.length > 0) {
            if (!visibleTabs.includes('4')) {
                visibleTabs.push('4');
            }
        }
        
        // Kararlar verisi varsa Tab 5'i göster
        if (arbitrationData?.rulingInformation && arbitrationData.rulingInformation !== null) {
            if (!visibleTabs.includes('5')) {
                visibleTabs.push('5');
            }
        }
        
        // Tekrar Başvuru verisi varsa Tab 6'yı göster
        if (arbitrationData?.reapplicationInformation && arbitrationData.reapplicationInformation !== null) {
            if (!visibleTabs.includes('6')) {
                visibleTabs.push('6');
            }
        }

        // Tabları sıralı döndür
        return visibleTabs.sort();
    };

    // Get visible tabs based on current state
    const visibleTabIds = getVisibleTabIds();
    const visibleTabs = tabs.filter(tab => visibleTabIds.includes(tab.id));

    const toggle = tab => {
        // Only allow switching to visible tabs
        if (visibleTabIds.includes(tab) && activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    // Fetch arbitration data
    const fetchArbitrationData = async () => {
        try {
            setLoading(true);
            const response = await fetchFormData({
                apiEndpoint: arbitrationApiUrl,
                page: 1,
                pageSize: 1,
            });
            
            console.log('Arbitration API Response:', response);
            
            // Extract data based on API response structure
            let data;
            if (response?.isSuccess && response?.data) {
                // New API format with isSuccess/data wrapper
                data = response.data;
            } else if (response) {
                // Old API format with direct data return
                data = response;
            } else {
                // No valid data
                toast.error('Tahkim bilgileri alınamadı');
                return;
            }
            
            if (data) {
                setArbitrationData(data);
            } else {
                toast.error('Tahkim bilgileri bulunamadı');
            }
        } catch (error) {
            console.error('Error fetching arbitration data:', error);
            toast.error('Tahkim bilgileri alınırken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchArbitrationData();
        }
    }, [id]);

    // Auto-select first visible tab if current active tab is not visible
    useEffect(() => {
        if (!visibleTabIds.includes(activeTab) && visibleTabIds.length > 0) {
            setActiveTab(visibleTabIds[0]);
        }
    }, [arbitrationData?.arbitrationState?.title]);

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs mainTitle="Tahkim Detayı" parent="Tahkim" title="Detay" />
                <Container fluid={true}>
                    <Row>
                        <Col sm="12">
                            <Card>
                                <CardBody>
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Yükleniyor...</span>
                                        </div>
                                        <p className="mt-3">Tahkim bilgileri yükleniyor...</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs 
                mainTitle="Tahkim Detayı" 
                parent="Tahkim" 
                title={`Tahkim #${id}`} 
            />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        {/* Current State Display */}
                        {arbitrationData?.arbitrationState && (
                            <div className="mb-3">
                                <div className="alert alert-info">
                                    <i className="fa fa-info-circle me-2"></i>
                                    <strong>Mevcut Durum:</strong> {arbitrationData.arbitrationState.title}
                                    <small className="ms-3 text-muted">
                                        Son Güncelleme: {new Date(arbitrationData.arbitrationState.lastModified).toLocaleString('tr-TR')}
                                    </small>
                                </div>
                            </div>
                        )}

                        {/* Tab Navigation */}
                        <Nav tabs className="nav-tabs-success mb-4">
                            {visibleTabs.map(tab => (
                                <NavItem key={tab.id}>
                                    <NavLink
                                        className={classnames({ active: activeTab === tab.id })}
                                        onClick={() => toggle(tab.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <i className={tab.icon} style={{ marginRight: '8px' }}></i>
                                        {tab.title}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        {/* Tab Content */}
                        <TabContent activeTab={activeTab}>
                            {/* Tab 1: Kaza Dosyaları - Always visible */}
                            {visibleTabIds.includes('1') && (
                                <TabPane tabId="1">
                                    <AccidentFileInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 2: Tahkim Bilgileri - Always visible */}
                            {visibleTabIds.includes('2') && (
                                <TabPane tabId="2">
                                    <ArbitrationInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 3: Ara Kararlar */}
                            {visibleTabIds.includes('3') && (
                                <TabPane tabId="3">
                                    <InterlocutoryRulingInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 4: Bilirkişi Raporları */}
                            {visibleTabIds.includes('4') && (
                                <TabPane tabId="4">
                                    <ExpertWitnessReportInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 5: Kararlar */}
                            {visibleTabIds.includes('5') && (
                                <TabPane tabId="5">
                                    <RulingInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}

                            {/* Tab 6: Tekrar Başvuru Bilgileri */}
                            {visibleTabIds.includes('6') && (
                                <TabPane tabId="6">
                                    <ReapplicationInformations 
                                        arbitrationId={id} 
                                        arbitrationData={arbitrationData}
                                        refreshArbitrationData={fetchArbitrationData}
                                    />
                                </TabPane>
                            )}
                        </TabContent>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default Arbitration;
